import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EnvelopeIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/20/solid";

import FormInput from "../../components/controls/FormInput";
import FormTextarea from "../../components/controls/FormTextArea";
import Button from "../../components/controls/Button";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("errors.nameRequired");
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = t("errors.emailInvalid");
    if (!formData.message.trim())
      newErrors.message = t("errors.messageRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Email/send-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      setSubmitted(res.ok);
      if (!res.ok) alert("Erreur lors de l'envoi du message.");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'envoi du message.");
    }
  };

  return (
    <div className="overflow-x-hidden">
      {/* Header plein écran */}
      <PageHeader
        title={t("contactPage.title")}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      {/* Contenu */}
      <div className="container mx-auto px-6 py-8 text-onWhite">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          {/* Formulaire */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">
              {t("contactPage.formTitle", "Nous écrire")}
            </h3>

            {submitted ? (
              <div className="text-center">
                <p className="text-green-600">
                  {t("contactPage.messageReceived")}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormInput
                  label={t("contactPage.nameLabel")}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("contactPage.namePlaceholder")}
                  error={errors.name}
                  required
                  icon={<UserIcon className="w-5 h-5 text-gray-500" />}
                />
                <FormInput
                  label={t("contactPage.emailLabel")}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("contactPage.emailPlaceholder")}
                  type="email"
                  error={errors.email}
                  required
                  icon={<EnvelopeIcon className="w-5 h-5 text-gray-500" />}
                />
                <FormTextarea
                  label={t("contactPage.messageLabel")}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t("contactPage.messagePlaceholder")}
                  as="textarea"
                  error={errors.message}
                  required
                  icon={<PencilSquareIcon className="w-5 h-5 text-gray-500" />}
                />

                {/* 🔁 Bouton avec ton composant */}
                <Button
                  as="button"
                  type="submit"
                  color="primary"
                  size="lg"
                  fullWidth
                >
                  {t("contactPage.submitButton")}
                </Button>
              </form>
            )}
          </div>

          {/* Informations de contact */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">
              {t("contactPage.contactInfoTitle")}
            </h3>
            <p>
              <EnvelopeIcon className="w-5 h-5 inline mr-2 text-primary ml-2" />
              information@ananassolutions.com
            </p>
            <p>
              <PhoneIcon className="w-5 h-5 inline mr-2 text-primary ml-2" />
              1-855-700-1721 {t("contactPage.withoutFees")}
            </p>
            <p>
              <PhoneIcon className="w-5 h-5 inline mr-2 text-primary ml-2" />
              450-700-1721
            </p>

            <div className="space-y-2">
              <p className="font-semibold ">
                {t("contactPage.socialMediaTitle")}
              </p>
              <p>
                <GlobeAltIcon className="w-5 h-5 inline mr-2 text-primary ml-2" />
                <a
                  href="https://www.facebook.com/ananassolutions.ca"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </p>
              <p>
                {/* icône YouTube inline */}
                <svg
                  className="w-5 h-5 inline mr-2 text-primary ml-2"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#000"
                    d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4 C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1 c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"
                  ></path>
                  <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                </svg>
                <a
                  href="https://www.youtube.com/@ananassolutions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Youtube
                </a>
              </p>
              <p>
                {/* icône TikTok inline */}
                <svg
                  className="w-5 h-5 inline mr-2 text-primary ml-2"
                  viewBox="0 0 50 50"
                >
                  <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z"></path>
                </svg>
                <a
                  href="https://www.tiktok.com/@ananas.solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tiktok
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
